import React from 'react';
import gql from 'graphql-tag';
import { graphql } from '@apollo/react-hoc';
import { compose } from 'react-recompose';
import get from 'lodash/get';
import { defineMessages } from 'react-intl';
import { CONNECTION_SUBSCRIPTION, REMOVE_DIVERSITY_FILTERS } from '../Authorization/constants';
import { onSubscriptionActive } from '../../utils/subscriptions';
import AMConfigureFrame from '../../components/AMConfigureFrame';
import { messages, hasDiversityEnabled } from '../AdvancedAwarenessPage';
import { SimpleLoadingComponent } from '../../utils/apollo';
import { withEnabledFeatures } from '../../utils/features';

import './styles.css';

export const query = gql`
  query AdvaAwareAMSubscriptions {
    viewer {
      id
      heAccount {
        id
        activeMatchSubscriptions {
          edges {
            node {
              audience {
                diversity
              }
            }
          }
        }
      }
    }
  }
`;

const OVERVIEW_PATHNAME = '/connection/overview';
const THRESHOLD_PATHNAME = '/connection/threshold';
const DIVERSITY_PATHNAME = '/connection/diversity';
const CONNECTIONS_PATHNAME = '/connection/connections';

const graphqlData = graphql(query, {
  props: (props) => {
    const edges = get(props, 'data.viewer.heAccount.activeMatchSubscriptions.edges', []);
    return {
      ...props,
      hasDiversityEnabled: hasDiversityEnabled(edges, props),
    };
  },
});

const ownMessages = defineMessages({
  connections: {
    id: 'am-config.menu.connections', defaultMessage: 'Connections',
  },
  header: {
    id: 'am-config.connection.header', defaultMessage: 'Intersect Connection',
  },
  subheader: {
    id: 'am-config.connection.subheader',
    defaultMessage: `Intersect Connection allows your institution to identify student characteristics that would
    be a best-fit and communicate to those students your interest in them. Students can then opt-in to connect
    with your institution in order to facilitate further communication.`,
  },
});

const removeDiversityFilters = (props) => {
  const enabledFeatures = get(props, 'ownProps.enabledFeatures') || [];
  return enabledFeatures.includes(REMOVE_DIVERSITY_FILTERS);
};

const menuConfig = (props) => {
  const config = [
    { to: OVERVIEW_PATHNAME, message: messages.overview },
    { to: THRESHOLD_PATHNAME, message: messages.threshold },
    { to: CONNECTIONS_PATHNAME, message: ownMessages.connections },
  ];
  if (!removeDiversityFilters(props)) {
    config.splice(2, 0, { to: DIVERSITY_PATHNAME, message: messages.diversity });
  }
  return config;
};

const secondMenuConfig = (props) => {
  const configureLinks = [
    { to: THRESHOLD_PATHNAME, message: messages.threshold },
  ];
  if (!removeDiversityFilters(props)) {
    configureLinks.push({ to: DIVERSITY_PATHNAME, message: messages.diversity });
  }
  return [
    {
      section: messages.overview,
      links: [{ to: OVERVIEW_PATHNAME, message: messages.understanding }],
    }, {
      section: messages.configure,
      links: configureLinks,
    },
  ];
};

const connectionsOnlyMenuConfig = [
  { to: CONNECTIONS_PATHNAME, message: ownMessages.connections },
];

const connectionsOnlySecondMenuConfig = [];

export const ConnectionsOnlyFrameComponent = (props) => (
  <AMConfigureFrame
    title="Connection"
    header={ownMessages.header}
    subheader={ownMessages.subheader}
    menuConfig={connectionsOnlyMenuConfig}
    secondMenuConfig={connectionsOnlySecondMenuConfig}
    hideSecondMenuRoutes={[CONNECTIONS_PATHNAME]}
    {...props}
  />
);

export const ConnectionConfigureFrameComponent = (props) => (
  <AMConfigureFrame
    title="Connection"
    header={ownMessages.header}
    subheader={ownMessages.subheader}
    menuConfig={menuConfig(props)}
    secondMenuConfig={secondMenuConfig(props)}
    hideSecondMenuRoutes={[CONNECTIONS_PATHNAME]}
    {...props}
  />
);

const fullyFeatured = SimpleLoadingComponent(compose(withEnabledFeatures, graphqlData))(ConnectionConfigureFrameComponent);

export default onSubscriptionActive(CONNECTION_SUBSCRIPTION, { failure: ConnectionsOnlyFrameComponent })(fullyFeatured);
